import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { useToast } from '../ToastContext';
import { useAsk } from '../AskContext';
import { fmt, cheapestOf, unconvertibleNote } from '../lib/money';

const REASONS = [
  'Best price',
  'Fastest lead time',
  'QPL compliant',
  'Factory new',
  'Approved manufacturer match',
  'Full coverage',
  'Lower MOQ impact',
  'Customer preference',
  'Manual override',
];

// ─── Filter definitions ────────────────────────────────────────────────────
// Each filter is { id, label, test(row) → bool }
const FILTERS = [
  { id: 'all', label: 'All' },
  {
    id: 'missing',
    label: 'Missing quotes',
    // row has at least one supplier with no quote
    test: ({ cellsBySupplier }) =>
      Object.values(cellsBySupplier).some((c) => !c.quoteLine),
  },
  {
    id: 'qpl',
    label: 'QPL risks',
    // at least one quoted cell where qplCompliant !== 'Yes'
    test: ({ cellsBySupplier }) =>
      Object.values(cellsBySupplier).some(
        (c) => c.quoteLine && c.quoteLine.qplCompliant !== 'Yes'
      ),
  },
  {
    id: 'alternates',
    label: 'Alternates',
    test: ({ cellsBySupplier }) =>
      Object.values(cellsBySupplier).some(
        (c) => c.quoteLine && c.quoteLine.isAlternate
      ),
  },
  // Award filters — `isDecided` comes from the committed Decision(s), not from
  // in-session picks, so these answer "what have we actually awarded?".
  {
    id: 'decided',
    label: 'Decided lines',
    test: ({ isDecided }) => !!isDecided,
  },
  {
    id: 'undecided',
    label: 'Undecided lines',
    test: ({ isDecided }) => !isDecided,
  },
  {
    id: 'moq',
    label: 'MOQ impact >5%',
    test: ({ cellsBySupplier, line }) =>
      Object.values(cellsBySupplier).some((c) => {
        if (!c.quoteLine) return false;
        const req = line.quantity || 1;
        const excess = c.quoteLine.excessQty || 0;
        return excess / req > 0.05;
      }),
  },
  {
    id: 'bestprice',
    label: 'Best price wins',
    // row has a cell that is cheapest AND QualityReady
    // `baseCurrency` and `rankedOnLandedCost` come from the row so this filter
    // ranks the same way the matrix and the backend do — it used to rank on raw
    // effectiveTotalCost, mixing currencies.
    test: ({ cellsBySupplier, rankedOnLandedCost, baseCurrency }) => {
      const priced = Object.values(cellsBySupplier)
        .filter((c) => c.quoteLine)
        .map((c) => c.quoteLine);
      const cheapest = cheapestOf(priced, baseCurrency, { preferLanded: !!rankedOnLandedCost });
      return cheapest != null && cheapest.qualityStatus === 'QualityReady';
    },
  },
  {
    id: 'fastlead',
    label: 'Fastest lead',
    // row has at least one quoted cell (any supplier has a lead time)
    test: ({ cellsBySupplier }) =>
      Object.values(cellsBySupplier).some(
        (c) => c.quoteLine && c.quoteLine.leadTimeValue != null
      ),
  },
];

// ─── Expiry acknowledgement wire format (#14) ──────────────────────────────
// The server's expired-price gate is opened ONLY by an exact code from its
// EXPIRY_ACKS set — see waivesExpiry() in backend src/services/decision.service.js
// (`{QUOTE_EXPIRED, EXPIRED_ACCEPTED, EXPIRY_ACCEPTED}`). It deliberately refuses
// to read prose, so the quality acknowledgement this tab already sends
// (`risk accepted: <buyer text>`) does not and must not waive expiry.
//
// Until this existed the UI sent NO code at all, so awarding an expired price was
// a hard dead end: the banner said an acknowledgement was required and there was
// nothing anywhere to click. A buyer whose supplier had re-confirmed the price by
// phone — routine — could not record the award in the tool at all.
//
// The code is what unlocks the gate; the buyer's own words travel beside it so the
// audit trail carries the actual justification, never a constant.
const EXPIRY_ACK_CODE = 'QUOTE_EXPIRED';
const EXPIRY_ACK_CODES = new Set(['QUOTE_EXPIRED', 'EXPIRED_ACCEPTED', 'EXPIRY_ACCEPTED']);
const EXPIRY_ACK_PREFIX = 'expiry accepted: ';

/** Same normalisation the server's ackCode() applies, so both agree on a match. */
function ackCode(entry) {
  return String(entry || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
}

/**
 * Split a committed award's `riskFlagsAccepted` into the two separate facts it can
 * carry. Without this, an expiry-only award rendered a "risk accepted" chip — a
 * quality claim nobody made — because the chip only tested for a non-empty array.
 */
export function splitAcknowledgements(entries) {
  const all = Array.isArray(entries) ? entries.filter(Boolean) : [];
  const expiry = all.filter(
    (e) =>
      EXPIRY_ACK_CODES.has(ackCode(e)) ||
      String(e).toLowerCase().startsWith(EXPIRY_ACK_PREFIX)
  );
  return { expiry, quality: all.filter((e) => !expiry.includes(e)) };
}

// ─── Layout options ────────────────────────────────────────────────────────
const LAYOUTS = [
  { id: 'supplier', label: 'Supplier-wise' },
  { id: 'line', label: 'Line-wise' },
  { id: 'split', label: 'Split' },
];

export default function Comparison({ rfqId, reload }) {
  // ── All hooks MUST come before any early returns ──────────────────────
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [activeFilters, setActiveFilters] = useState([]);
  const [layout, setLayout] = useState('supplier');
  const [hideNoQuote, setHideNoQuote] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [colDropOpen, setColDropOpen] = useState(false);
  const colDropRef = useRef(null);
  // Line scope
  const [linePickerOpen, setLinePickerOpen] = useState(false);
  const [scopedLineIds, setScopedLineIds] = useState(new Set());
  // Inline-decision state: lineId -> [{ supplierId, quoteLineId, quantity, reason }]
  // Multiple picks per line allowed, e.g. 50 to A + 50 to B.
  const [picks, setPicks] = useState({});
  const [committing, setCommitting] = useState(false);
  // Revision context handed over by the Decide tab (Feature B). Null in the
  // ordinary "award something new" flow.
  const [revision, setRevision] = useState(null);
  const toast = useToast();
  const ask = useAsk();

  useEffect(() => {
    const ids = scopedLineIds.size > 0 ? [...scopedLineIds] : undefined;
    api.comparison(rfqId, ids).then(setData).catch((e) => setErr(e.message));
  }, [rfqId, JSON.stringify([...scopedLineIds].sort())]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Pick up a revision started on the Decide tab and pre-select the existing
   * awards, so the user edits the award instead of rebuilding it from scratch.
   */
  // Keyed on the revise ID, NOT on `searchParams`.
  //
  // `searchParams` gets a fresh identity on every setSearchParams call, and hiding
  // a supplier column does exactly that — so this effect re-ran and re-seeded
  // picks from the prefill, silently discarding the quantities the buyer had just
  // edited. The next commit then wrote the ORIGINAL amounts as the new revision.
  const reviseId = searchParams.get('revise');
  useEffect(() => {
    if (!reviseId) return;
    let ctx = null;
    try {
      ctx = JSON.parse(sessionStorage.getItem(`rivet_revision_${rfqId}`) || 'null');
    } catch (_) { /* malformed — fall through to the normal flow */ }
    if (!ctx?.revisesDecisionId) return;
    setRevision(ctx);
    const seeded = {};
    for (const s of ctx.prefillSelections || []) {
      const lid = String(s.rfqLineItemId);
      if (!seeded[lid]) seeded[lid] = [];
      seeded[lid].push({
        supplierId: String(s.supplierId),
        quoteLineId: s.quoteLineId ? String(s.quoteLineId) : null,
        quantity: s.quantity ?? 0,
        reason: s.reason || '',
      });
    }
    setPicks(seeded);
  }, [rfqId, reviseId]);

  /** Quantity on this line freed up by the award being revised. */
  function revisedQtyFreed(lineId) {
    if (!revision) return 0;
    const mine = (revision.prefillSelections || []).filter(
      (s) => String(s.rfqLineItemId) === String(lineId)
    );
    const line = data?.lineMatrix?.find(({ line: l }) => String(l._id) === String(lineId))?.line;
    const decided = Number(line?.decidedQuantity) || 0;
    const freed = mine.reduce((a, s) => a + (Number(s.quantity) || 0), 0);
    return Math.max(0, decided - freed);
  }

  function cancelRevision() {
    sessionStorage.removeItem(`rivet_revision_${rfqId}`);
    setRevision(null);
    setPicks({});
    setSearchParams((sp) => {
      const next = new URLSearchParams(sp);
      next.delete('revise');
      return next;
    });
  }

  // Close column dropdown on outside click
  useEffect(() => {
    function onOutside(e) {
      if (colDropRef.current && !colDropRef.current.contains(e.target)) {
        setColDropOpen(false);
      }
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  // Parse hidden supplier IDs from URL search params
  const hiddenSuppliers = useMemo(() => {
    const raw = searchParams.get('hide') || '';
    return new Set(raw ? raw.split(',').filter(Boolean) : []);
  }, [searchParams]);

  // Visible suppliers (after hide toggle)
  const visibleSuppliers = useMemo(() => {
    if (!data) return [];
    return data.supplierSummaries.filter(
      (s) => !hiddenSuppliers.has(String(s.supplier._id))
    );
  }, [data, hiddenSuppliers]);

  // Apply filters to lineMatrix rows
  const filteredRows = useMemo(() => {
    if (!data) return [];
    let rows = data.lineMatrix;

    // Hide rows where every visible supplier has no quote
    if (hideNoQuote) {
      rows = rows.filter(({ cellsBySupplier }) =>
        visibleSuppliers.some(
          (s) => cellsBySupplier[String(s.supplier._id)]?.quoteLine
        )
      );
    }

    // Apply active filter predicates (AND-stacked)
    const activeDefs = FILTERS.filter(
      (f) => f.id !== 'all' && activeFilters.includes(f.id)
    );
    if (activeDefs.length === 0) return rows;
    // Each row carries the RFQ currency so a filter can rank comparably; the
    // backend publishes it once on the summary, not per row.
    const withCurrency = rows.map((row) => ({ ...row, baseCurrency: data.summary.baseCurrency }));
    return withCurrency.filter((row) => activeDefs.every((f) => f.test(row)));
  }, [data, activeFilters, hideNoQuote, visibleSuppliers]);

  // Aggregate balance across all lines — must run before any early return
  // since it's a hook. Returns zeros when data is not yet loaded.
  const balance = useMemo(() => {
    if (!data) return { totalRequired: 0, totalDecided: 0, totalPickedNow: 0, totalOutstanding: 0, lineCount: 0, linesWithPicks: 0 };
    let totalRequired = 0, totalDecided = 0, totalPickedNow = 0, linesWithPicks = 0;
    for (const { line } of data.lineMatrix) {
      totalRequired += Number(line.quantity) || 0;
      totalDecided += Number(line.decidedQuantity) || 0;
      const lid = String(line._id);
      const pickedNow = (picks[lid] || []).reduce((acc, p) => acc + (Number(p.quantity) || 0), 0);
      totalPickedNow += pickedNow;
      if (pickedNow > 0) linesWithPicks++;
    }
    const totalOutstanding = Math.max(0, totalRequired - totalDecided - totalPickedNow);
    return { totalRequired, totalDecided, totalPickedNow, totalOutstanding, lineCount: data.lineMatrix.length, linesWithPicks };
  }, [data, picks]);

  // ── Early returns (after all hooks) ──────────────────────────────────
  if (err) return <div className="banner risk">{err}</div>;
  if (!data) return <div className="empty">Loading comparison…</div>;
  // The RFQ's own currency. EVERY money figure on this tab is in it, and every
  // formatter takes it explicitly — the old formatter hardcoded USD, so an EUR
  // RFQ rendered EUR totals with a dollar sign and nothing said otherwise.
  const baseCurrency = data.summary.baseCurrency;
  if (data.supplierSummaries.length === 0)
    return (
      <div className="empty">
        <h3>No responses yet</h3>
        <p>Enter quotes in the Responses tab.</p>
      </div>
    );

  const reco = data.summary.recommended;

  // ── Helpers ─────────────────────────────────────────────────────────
  function toggleSupplierCol(supplierId) {
    const id = String(supplierId);
    const next = new Set(hiddenSuppliers);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    const sp = new URLSearchParams(searchParams);
    if (next.size > 0) sp.set('hide', [...next].join(','));
    else sp.delete('hide');
    setSearchParams(sp, { replace: true });
  }

  function toggleFilter(filterId) {
    if (filterId === 'all') {
      setActiveFilters([]);
      return;
    }
    setActiveFilters((prev) =>
      prev.includes(filterId)
        ? prev.filter((f) => f !== filterId)
        : [...prev, filterId]
    );
  }

  // ── Inline decision helpers ─────────────────────────────────────────
  function lineRequiredQty(line) { return Number(line.quantity) || 0; }
  function lineDecidedQty(line) { return Number(line.decidedQuantity) || 0; }
  /**
   * Quantity still awardable on this line.
   *
   * In REVISION mode the quantity held by the decision being revised is released —
   * that is the whole point of revising it. Using the committed decidedQuantity
   * here made a fully-awarded line compute remaining = 0, which switched off
   * `canPick`, which removed BOTH the quantity input and the "×" remove button. The
   * buyer could then neither clear the pre-selected awards nor choose differently:
   * the only options were to re-commit the identical award or cancel. The commit
   * path already accounted for the freed quantity (see revisedQtyFreed below), so
   * only the on-screen gating was wrong.
   */
  function lineRemaining(line) {
    const taken = revision ? revisedQtyFreed(line._id) : lineDecidedQty(line);
    return Math.max(0, lineRequiredQty(line) - taken);
  }

  // Toggle a supplier pick on a line. If already picked, unpick.
  // If not picked, add a new pick with default qty = whatever's left after current picks.
  function togglePick(line, supplierId, quoteLineId) {
    const lid = String(line._id);
    setPicks((prev) => {
      const cur = prev[lid] || [];
      const idx = cur.findIndex((p) => String(p.supplierId) === String(supplierId));
      if (idx >= 0) {
        const next = [...cur.slice(0, idx), ...cur.slice(idx + 1)];
        return { ...prev, [lid]: next };
      }
      const alreadyPicked = cur.reduce((acc, p) => acc + (Number(p.quantity) || 0), 0);
      const remaining = Math.max(0, lineRemaining(line) - alreadyPicked);
      return {
        ...prev,
        [lid]: [...cur, { supplierId: String(supplierId), quoteLineId: String(quoteLineId), quantity: remaining, reason: 'Best price' }],
      };
    });
  }

  function updatePick(lineId, supplierId, patch) {
    setPicks((prev) => {
      const cur = prev[lineId] || [];
      return {
        ...prev,
        [lineId]: cur.map((p) => (String(p.supplierId) === String(supplierId) ? { ...p, ...patch } : p)),
      };
    });
  }

  /**
   * The comparison cell for a (line, supplier) pair — quote line, validity and
   * award in one object. Both the acknowledgement pass and the selection-building
   * pass below must read validity from the SAME place, or the UI could ask about an
   * expiry it then fails to declare (or vice versa).
   */
  function cellFor(lineId, supplierId) {
    const row = data.lineMatrix.find(({ line: l }) => String(l._id) === String(lineId));
    return (row?.cellsBySupplier || {})[supplierId] || null;
  }

  function supplierNameOf(supplierId) {
    return (
      data.supplierSummaries?.find((s) => String(s.supplier._id) === String(supplierId))?.supplier
        ?.name || 'this supplier'
    );
  }

  async function commitInlineDecision() {
    // Revising replaces a prior award, so the reason is collected here — at the
    // point the final awards are known — and the API requires it (Feature B).
    let revisionReason = null;
    if (revision) {
      revisionReason = await ask({
        title: `Committing as Rev ${revision.nextRevision}`,
        body: 'Why is the award changing?',
        placeholder: 'e.g. surplus stock rejected by quality, supplier withdrew',
        requireText: true,
        confirmLabel: `Commit Rev ${revision.nextRevision}`,
      });
      // The dialog cannot return blank — the confirm button stays disabled until
      // something is typed — so cancellation is the only null case.
      if (revisionReason == null) return;
    }

    // Risky lines need an explicit, typed acknowledgement per line (R2).
    //
    // This used to send riskFlagsAccepted: ['risk-accepted'] automatically for any
    // QualityRisk line. The server gate blocks a commit unless the risk is
    // acknowledged, so auto-filling it satisfied the exact precondition the gate
    // tests and made the gate unreachable — a QualityRisk line committed with no
    // warning and nothing recorded. The buyer now has to say why, in their own
    // words, and that text is what lands in the audit trail.
    //
    // An EXPIRED price is collected in the same pass but acknowledged separately
    // below: a lapsed price and a quality risk are different facts about different
    // things, and the server keeps them apart on purpose (waivesExpiry vs
    // waivesQualityRisk). Asking once and reusing the answer would mean the buyer
    // waived a price they were never shown.
    const riskyPicks = [];
    const expiredPicks = [];
    for (const { line } of data.lineMatrix) {
      const lid = String(line._id);
      for (const p of picks[lid] || []) {
        // Must use the SAME predicate as the selections loop below (`q <= 0`).
        // `!Number(p.quantity)` treated a negative quantity as pickable, so a
        // risky line with qty -5 raised an acknowledgement dialog for a line the
        // commit then dropped — a reason collected against nothing.
        if (!(Number(p.quantity) > 0)) continue;
        const cell = cellFor(lid, p.supplierId);
        const ql = cell?.quoteLine;
        if (ql?.qualityStatus === 'QualityRisk') {
          riskyPicks.push({ lid, supplierId: p.supplierId, line, quoteLine: ql });
        }
        // Only 'Expired' blocks. 'ExpiringSoon' is a nudge the cell already shows
        // and the server does not gate on it, so prompting for it would train
        // buyers to click through acknowledgement dialogs.
        if (cell?.validity?.state === 'Expired') {
          expiredPicks.push({ lid, supplierId: p.supplierId, line, validity: cell.validity });
        }
      }
    }

    const riskAcks = {};
    for (const rp of riskyPicks) {
      const supplierName = supplierNameOf(rp.supplierId);
      const ack = await ask({
        title: `Line ${rp.line.lineNumber} — quality risk`,
        body:
          `${supplierName} is a QUALITY RISK on this line:\n\n` +
          `${rp.quoteLine.qualityRule || 'Quality risk'}\n\n` +
          'Awarding it anyway is recorded in the audit trail against your name. ' +
          'Cancel to pick a different supplier instead.',
        placeholder: 'Why is this risk acceptable?',
        requireText: true,
        danger: true,
        confirmLabel: 'Accept risk and award',
      });
      if (ack == null || !ack.trim()) {
        toast.push(
          `Line ${rp.line.lineNumber} is a quality risk and was not acknowledged — nothing committed.`,
          { variant: 'error' }
        );
        return;
      }
      riskAcks[`${rp.lid}:${rp.supplierId}`] = ack.trim();
    }

    // Expired prices: one dialog per affected pick, asked AFTER the quality ones
    // so a line that is both gets both questions rather than one merged prompt.
    // Still before setCommitting(true) — the button must not read "Committing…"
    // while the buyer is being asked whether to commit at all.
    const expiryAcks = {};
    for (const ep of expiredPicks) {
      const supplierName = supplierNameOf(ep.supplierId);
      const ack = await ask({
        title: `Line ${ep.line.lineNumber} — price expired`,
        body:
          `${supplierName}'s price on this line has lapsed:\n\n` +
          `${ep.validity.label}\n\n` +
          'Award it only if the supplier has re-confirmed the price. Your reason is ' +
          'recorded in the audit trail against your name. Cancel to re-quote instead.',
        placeholder: 'e.g. supplier re-confirmed by phone, valid to month end',
        requireText: true,
        danger: true,
        confirmLabel: 'Accept expired price and award',
      });
      if (ack == null || !ack.trim()) {
        toast.push(
          `Line ${ep.line.lineNumber} has an expired price and was not acknowledged — nothing committed.`,
          { variant: 'error' }
        );
        return;
      }
      expiryAcks[`${ep.lid}:${ep.supplierId}`] = ack.trim();
    }

    setCommitting(true);
    try {
      const selections = [];
      for (const { line } of data.lineMatrix) {
        const lid = String(line._id);
        const pks = picks[lid] || [];
        const requiredQty = lineRequiredQty(line);
        // When revising, the quantity held by the decision being replaced is
        // freed — otherwise revising a full-quantity award would always look
        // like an overshoot. The server re-checks this authoritatively.
        const decidedQty = revision ? revisedQtyFreed(lid) : lineDecidedQty(line);
        const remaining = Math.max(0, requiredQty - decidedQty);
        // Validate: sum of picks for this line must not exceed remaining
        const sum = pks.reduce((a, p) => a + (Number(p.quantity) || 0), 0);
        if (sum > remaining) {
          throw new Error(`Line ${line.lineNumber}: picked ${sum} exceeds remaining ${remaining}`);
        }
        for (const p of pks) {
          const q = Number(p.quantity);
          if (!q || q <= 0) continue;
          const cell = cellFor(lid, p.supplierId);
          const ql = cell?.quoteLine;
          const isRisk = ql?.qualityStatus === 'QualityRisk';
          const isExpired = cell?.validity?.state === 'Expired';
          // The acknowledgements the buyer actually typed, never a constant.
          const ack = riskAcks[`${lid}:${p.supplierId}`];
          const expiryAck = expiryAcks[`${lid}:${p.supplierId}`];
          // One array, up to two independent acknowledgements. A line that is both a
          // quality risk and expired carries both — dropping either would leave the
          // other gate blocking, and the buyer was asked about both above.
          const riskFlagsAccepted = [];
          if (isRisk && ack) riskFlagsAccepted.push(`risk accepted: ${ack}`);
          // The bare code is what the server's EXPIRY_ACKS set matches; the prose
          // beside it is what an auditor actually needs to read.
          if (isExpired && expiryAck) {
            riskFlagsAccepted.push(EXPIRY_ACK_CODE, `${EXPIRY_ACK_PREFIX}${expiryAck}`);
          }
          selections.push({
            rfqLineItemId: lid,
            supplierId: p.supplierId,
            quoteLineId: p.quoteLineId,
            quantity: q,
            reason: p.reason || 'Manual override',
            riskFlagsAccepted,
          });
        }
      }
      if (selections.length === 0) {
        throw new Error('No selections — pick a supplier on at least one line first');
      }
      // Use Split mode so each selection's per-cell reason is preserved.
      // overallReason is required by the API for non-Split mode but optional here;
      // we synthesize a summary from the per-pick reasons.
      const uniqReasons = [...new Set(selections.map((s) => s.reason).filter(Boolean))];
      const overallReason = uniqReasons.length === 1 ? uniqReasons[0] : 'Compare-tab decision';
      await api.commitDecision(rfqId, {
        mode: 'Split',
        overallReason,
        selections,
        ...(revision
          ? { revisesDecisionId: revision.revisesDecisionId, revisionReason: revisionReason.trim() }
          : {}),
      });
      if (revision) {
        toast.push(`Committed as Rev ${revision.nextRevision} — Rev ${revision.nextRevision - 1} kept for audit`);
        sessionStorage.removeItem(`rivet_revision_${rfqId}`);
        setRevision(null);
        setSearchParams((sp) => {
          const next = new URLSearchParams(sp);
          next.delete('revise');
          return next;
        });
      } else {
        toast.push(`Decision committed (${selections.length} selection${selections.length !== 1 ? 's' : ''})`);
      }
      setPicks({});
      // Refresh BOTH scopes: this tab's comparison payload for the new
      // decidedQuantity, and the RFQ shell so the header status stops reading
      // "PartialQuotes" once every line is awarded (R6). The shell owns the RFQ
      // document, so refetching only the comparison left it stale until a manual
      // page reload.
      const ids = scopedLineIds.size > 0 ? [...scopedLineIds] : undefined;
      api.comparison(rfqId, ids)
        .then(setData)
        .catch((e) => toast.push(`Committed, but the view could not refresh: ${e.message}`, { variant: 'error' }));
      if (typeof reload === 'function') {
        Promise.resolve(reload()).catch(() => { /* header staleness is not worth an error toast */ });
      }
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    } finally {
      setCommitting(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div>
      {/* Lines nobody will quote (#15) — these need a new source, not chasing. */}
      {data.summary.fullyDeclinedLineCount > 0 && (
        <div className="banner risk" style={{ marginBottom: 16 }}>
          <b>{data.summary.fullyDeclinedLineCount} line
          {data.summary.fullyDeclinedLineCount === 1 ? '' : 's'} declined by every supplier.</b>{' '}
          These need a new source — chasing the current ones will not help.
        </div>
      )}

      {/* Stale prices in the set (#14) — surfaced before anything is picked. */}
      {data.summary.expiredSupplierCount > 0 && (
        <div className="banner risk" style={{ marginBottom: 16 }}>
          <b>{data.summary.expiredSupplierCount} supplier
          {data.summary.expiredSupplierCount === 1 ? '' : 's'} quoting on an expired price.</b>{' '}
          Re-confirm with the supplier first. You can still award one — committing will
          ask you to record why the lapsed price is acceptable.
        </div>
      )}
      {data.summary.expiredSupplierCount === 0 && data.summary.expiringSoonSupplierCount > 0 && (
        <div className="banner review" style={{ marginBottom: 16 }}>
          {data.summary.expiringSoonSupplierCount} quote
          {data.summary.expiringSoonSupplierCount === 1 ? '' : 's'} expiring within a few days.
        </div>
      )}

      {/* Revision mode is a different intent from "award something new", so say
          so plainly and list who has already been told they won. */}
      {revision && (
        <div className="banner review" style={{ marginBottom: 16 }}>
          <b>Revising the decision — will commit as Rev {revision.nextRevision}.</b>{' '}
          The current awards are pre-selected below; adjust them and commit. Rev{' '}
          {revision.nextRevision - 1} is kept in the audit trail.
          {(revision.orderConfirmationsSent || []).length > 0 && (
            <div style={{ marginTop: 6 }}>
              Already confirmed to:{' '}
              <b>{revision.orderConfirmationsSent.map((s) => s.name).join(', ')}</b> — they will need re-notifying.
            </div>
          )}
        </div>
      )}
      {/* Layer 1 — RFQ summary */}
      <div className="summary-strip">
        <div className="stat">
          <div className="v">{data.summary.totalLines}</div>
          <div className="k">Lines</div>
        </div>
        <div className="stat">
          <div className="v">{data.summary.suppliersContacted}</div>
          <div className="k">Suppliers · {data.summary.responded} responded</div>
        </div>
        <div className="stat">
          <div className="v">{data.summary.coveragePct}%</div>
          <div className="k">Coverage</div>
        </div>
        {/* Only compare totals between suppliers that quoted every line in
            scope — a bare min() rewarded whoever quoted the fewest lines. */}
        <div className="stat">
          <div className="v">
            {data.summary.lowestComparableTotal != null
              ? formatMoney(data.summary.lowestComparableTotal, baseCurrency)
              : '—'}
          </div>
          <div className="k">
            {data.summary.lowestComparableTotal != null
              ? `Lowest full-coverage total · ${data.summary.comparableSupplierCount} of ${data.supplierSummaries.length} comparable`
              : 'No full-coverage quote yet'}
          </div>
          {/* Name the suppliers held back purely for a missing exchange rate.
              They used to be counted as comparable, so a supplier who quoted every
              line but priced half of them in an unconvertible currency could win
              this tile with half a total. Excluding them silently is almost as bad:
              the buyer cannot tell a rate is all that is missing. */}
          {data.summary.excludedForFxCount > 0 && (
            <div className="k" style={{ color: 'var(--ink-500)' }}>
              {data.summary.excludedForFxCount} full-coverage supplier
              {data.summary.excludedForFxCount === 1 ? '' : 's'} excluded — no exchange rate on some lines
            </div>
          )}
        </div>
        <div className="stat">
          {/* The WORST line, not the average. A package ships when its longest
              line arrives, so ranking on a mean chose the wrong supplier:
              [0,0,0,0,60] days averages to 12 and beat [21×5] at 21, although the
              first cannot complete for 60 days. */}
          <div className="v">{formatLeadDays(data.summary.fastestCompleteLeadDays ?? data.summary.fastestAvgLeadDays)}</div>
          <div className="k">
            Fastest complete
            {data.summary.fastestAvgLeadDays != null && (
              <span style={{ color: 'var(--ink-500)' }}> · avg {formatLeadDays(data.summary.fastestAvgLeadDays)}</span>
            )}
          </div>
        </div>
        {/* Delivered-cost headline (#13). Distinct tile from "lowest total" so an
            ex-works figure is never mistaken for a landed one. */}
        <div className="stat">
          <div className="v">
            {data.summary.lowestLandedTotal != null
              ? formatMoney(data.summary.lowestLandedTotal, baseCurrency)
              : '—'}
          </div>
          <div className="k">
            {data.summary.lowestLandedTotal != null
              ? `Lowest landed cost · ${data.summary.landedComparableSupplierCount} comparable`
              : data.summary.landedCostConfigured
              ? 'Landed cost needs incoterm + origin on every line'
              : 'Landed cost — no freight rates configured'}
          </div>
        </div>
        {/* Say which currency the totals are in, and warn when any quote could
            not be converted into it (#09). */}
        {(data.summary.isMultiCurrency || data.summary.unconvertibleLineCount > 0) && (
          <div className="stat">
            <div className="v" style={{ fontSize: 16 }}>{data.summary.baseCurrency}</div>
            <div className="k">
              Totals in {data.summary.baseCurrency}
              {data.summary.quotedCurrencies?.length > 1 && (
                <> · quoted in {data.summary.quotedCurrencies.join(', ')}</>
              )}
              {data.summary.unconvertibleLineCount > 0 && (
                <>
                  {' '}
                  <span className="chip risk" style={{ fontSize: 10 }}>
                    {data.summary.unconvertibleLineCount} line
                    {data.summary.unconvertibleLineCount === 1 ? '' : 's'} missing a rate
                  </span>
                </>
              )}
            </div>
          </div>
        )}
        <div className="stat">
          <div className="v">{reco?.name || '—'}</div>
          <div className="k">
            <span className="chip info">Recommended</span>
          </div>
        </div>
      </div>

      {/* Layer 2 — supplier cards */}
      <div className="supplier-cards">
        {data.supplierSummaries.map((s) => {
          const isReco =
            reco && String(reco.supplierId) === String(s.supplier._id);
          // Badge = the supplier's WORST line, not an average (R3).
          //
          // This was thresholded off qualityReadyRatio, which inverted the meaning
          // for two real cases: a supplier whose lines were all merely
          // ReviewRequired scored 0 and showed "Risk", while a supplier with one
          // genuine QualityRisk line among Ready ones scored ~0.8 and showed
          // "Review". One unacceptable line makes the supplier unacceptable for
          // that line, so the badge follows severity. The ratio is still shown
          // below as "Quality ready %", which is the figure an average suits.
          const riskLevel =
            s.worstQualityStatus === 'QualityRisk'
              ? 'risk'
              : s.worstQualityStatus === 'ReviewRequired'
              ? 'review'
              : s.worstQualityStatus === 'QualityReady'
              ? 'ready'
              : 'review';
          return (
            <div key={s.envelope._id} className="supplier-card">
              <div className="name">
                {s.supplier.name}
                {isReco && <span className="chip info">⭐ Recommended</span>}
              </div>
              <div className="row2">
                <div>Coverage <b>{Math.round(s.coveragePct * 100)}%</b></div>
                <div>
                  Total <b>{formatMoney(s.totalQuoted, baseCurrency)}</b>{' '}
                  {/* Make partial totals self-labelling so they can't be read
                      as a like-for-like figure. */}
                  {/* `totalQuoted` sums only CONVERTIBLE lines while
                      `quotedLineCount` counts every quoted line, so a supplier who
                      priced 5 of 10 lines unconvertibly showed a 5-line total
                      labelled "10 of 10 lines". Label what the total actually
                      covers, and name the gap. */}
                  <span style={{ color: 'var(--ink-500)', fontSize: 11 }}>
                    ({s.quotedLineCount - (s.unconvertibleLineCount || 0)} of {s.scopedLineCount} lines priced)
                  </span>
                  {s.unconvertibleLineCount > 0 && (
                    <span className="chip risk" style={{ fontSize: 9, marginLeft: 4 }}>
                      {s.unconvertibleLineCount} no rate
                    </span>
                  )}
                </div>
                <div>
                  {/* Worst line first — that is the date the order completes. */}
                  Lead <b>{formatLeadDays(s.worstLeadDays ?? s.avgLeadDays)}</b>
                  {s.avgLeadDays != null && s.worstLeadDays != null && s.worstLeadDays !== s.avgLeadDays && (
                    <span style={{ color: 'var(--ink-500)', fontSize: 11 }}> (avg {formatLeadDays(s.avgLeadDays)})</span>
                  )}
                  {s.lowLeadConfidenceLineCount > 0 && (
                    <span className="chip review" style={{ fontSize: 9, marginLeft: 4 }}>
                      {s.lowLeadConfidenceLineCount} low confidence
                    </span>
                  )}
                </div>
                {/* Delivered cost (#13): an ex-works total is not what the part
                    actually costs to receive. Shown only when every quoted line
                    has a complete freight/duty estimate. */}
                <div>
                  Landed{' '}
                  {s.totalLanded != null ? (
                    <>
                      <b>{formatMoney(s.totalLanded, baseCurrency)}</b>
                      {s.totalBuyerAddedCost > 0 && (
                        <span style={{ color: 'var(--ink-500)', fontSize: 11 }}>
                          {' '}(+{formatMoney(s.totalBuyerAddedCost, baseCurrency)})
                        </span>
                      )}
                    </>
                  ) : (
                    <span style={{ color: 'var(--ink-500)' }}>
                      — {s.quotedLineCount > 0 ? `${s.landedCompleteLineCount}/${s.quotedLineCount} priced` : ''}
                    </span>
                  )}
                </div>
                {(s.quoteIncoterms?.length > 0 || s.quoteOrigins?.length > 0) && (
                  <div style={{ color: 'var(--ink-500)', fontSize: 11 }}>
                    {[s.quoteIncoterms?.join('/'), s.quoteOrigins?.join('/')].filter(Boolean).join(' · ')}
                  </div>
                )}
                <div>QPL ready <b>{s.qplReady}/{s.quoted}</b></div>
                <div>CoC avail <b>{s.cocReady}/{s.quoted}</b></div>
                <div>
                  Quality ready <b>{Math.round(s.qualityReadyRatio * 100)}%</b>
                  {/* Name the line count driving the badge, so "Risk" on an
                      otherwise-good supplier is self-explanatory. */}
                  {s.riskLineCount > 0 ? (
                    <span style={{ color: 'var(--ink-500)' }}> · {s.riskLineCount} risk</span>
                  ) : s.reviewLineCount > 0 ? (
                    <span style={{ color: 'var(--ink-500)' }}> · {s.reviewLineCount} review</span>
                  ) : null}
                </div>
              </div>
              <span className={'chip ' + riskLevel}>
                {riskLevel === 'ready' ? 'Ready' : riskLevel === 'review' ? 'Review' : 'Risk'}
              </span>
            </div>
          );
        })}
      </div>

      {/* Line scope chip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span
          className={'chip ' + (scopedLineIds.size > 0 ? 'info' : 'muted')}
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setLinePickerOpen((o) => !o)}
        >
          {scopedLineIds.size > 0
            ? `Showing ${scopedLineIds.size} of ${data.summary.totalLines} lines (line scope)`
            : `Showing all ${data.summary.totalLines} lines`}
          {' '}▾
        </span>
        {scopedLineIds.size > 0 && (
          <button className="btn btn-ghost btn-sm" onClick={() => setScopedLineIds(new Set())}>Clear scope</button>
        )}
        {linePickerOpen && (
          <div style={{ position: 'absolute', background: '#fff', border: '1px solid var(--border-1)', borderRadius: 8, padding: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 100 }}>
            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Filter to lines:</div>
            {data.lineMatrix.map(({ line }) => (
              <label key={line._id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0' }}>
                <input
                  type="checkbox"
                  checked={scopedLineIds.size === 0 || scopedLineIds.has(String(line._id))}
                  onChange={() => {
                    setScopedLineIds((prev) => {
                      // if currently "all", switch to explicit set minus this one
                      const base = prev.size === 0
                        ? new Set(data.lineMatrix.map(({ line: l }) => String(l._id)))
                        : new Set(prev);
                      if (base.has(String(line._id))) base.delete(String(line._id));
                      else base.add(String(line._id));
                      // if all selected, go back to "all"
                      if (base.size === data.lineMatrix.length) return new Set();
                      return base;
                    });
                  }}
                />
                <span className="mono" style={{ fontSize: 12 }}>#{line.lineNumber}</span>
                <span style={{ fontSize: 12 }}>{line.partNumber}</span>
              </label>
            ))}
            <button className="btn btn-sm" style={{ marginTop: 8, width: '100%' }} onClick={() => setLinePickerOpen(false)}>Done</button>
          </div>
        )}
      </div>

      {/* Layer 3 — filter toolbar */}
      <div className="filter-bar">
        {/* Filter chips */}
        {FILTERS.map((f) => {
          const isAll = f.id === 'all';
          const isActive = isAll
            ? activeFilters.length === 0
            : activeFilters.includes(f.id);
          return (
            <span
              key={f.id}
              className={'chip ' + (isActive ? 'primary' : 'muted')}
              onClick={() => toggleFilter(f.id)}
            >
              {f.label}
            </span>
          );
        })}

        <span className="spacer" />

        {/* Hide-no-quote checkbox */}
        <label className="cmp-toggle-label">
          <input
            type="checkbox"
            checked={hideNoQuote}
            onChange={(e) => setHideNoQuote(e.target.checked)}
          />
          {' '}Hide lines no supplier quoted
        </label>

        {/* Columns dropdown */}
        <div className="col-dropdown-wrap" ref={colDropRef}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setColDropOpen((o) => !o)}
          >
            Columns{hiddenSuppliers.size > 0 ? ` (${hiddenSuppliers.size} hidden)` : ''}
          </button>
          {colDropOpen && (
            <div className="col-dropdown">
              {data.supplierSummaries.map((s) => {
                const sid = String(s.supplier._id);
                return (
                  <label key={sid} className="col-dropdown-item">
                    <input
                      type="checkbox"
                      checked={!hiddenSuppliers.has(sid)}
                      onChange={() => toggleSupplierCol(sid)}
                    />
                    {' '}{s.supplier.name}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Layout toggle */}
        <div className="layout-toggle">
          {LAYOUTS.map((l) => (
            <button
              key={l.id}
              className={'btn btn-sm ' + (layout === l.id ? '' : 'btn-secondary')}
              onClick={() => setLayout(l.id)}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {/* Layer 4 — matrix (layout-aware) */}
      {layout === 'split' ? (
        <div className="matrix-split">
          <div className="matrix-split-pane">
            <div className="matrix-split-label">Supplier-wise</div>
            <SupplierWiseTable rows={filteredRows} suppliers={visibleSuppliers} picks={picks} togglePick={togglePick} updatePick={updatePick} lineRemaining={lineRemaining} baseCurrency={data.summary.baseCurrency} />
          </div>
          <div className="matrix-split-pane">
            <div className="matrix-split-label">Line-wise</div>
            <LineWiseTable rows={filteredRows} suppliers={visibleSuppliers} baseCurrency={baseCurrency} />
          </div>
        </div>
      ) : layout === 'line' ? (
        <LineWiseTable rows={filteredRows} suppliers={visibleSuppliers} baseCurrency={baseCurrency} />
      ) : (
        <SupplierWiseTable rows={filteredRows} suppliers={visibleSuppliers} picks={picks} togglePick={togglePick} updatePick={updatePick} lineRemaining={lineRemaining} baseCurrency={data.summary.baseCurrency} />
      )}

      {/* Sticky decision footer — shows running tally + commit */}
      {balance.totalPickedNow > 0 && (
        <div style={{
          position: 'sticky', bottom: 0, marginTop: 20, marginLeft: -20, marginRight: -20, padding: '12px 20px',
          background: '#fff', borderTop: '2px solid var(--primary)', boxShadow: '0 -4px 16px rgba(0,0,0,0.08)', zIndex: 30,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="chip primary" style={{ fontSize: 12 }}>
                {balance.linesWithPicks} line{balance.linesWithPicks !== 1 ? 's' : ''} this commit
              </span>
              <span className="chip muted" style={{ fontSize: 12 }}>
                Picking {balance.totalPickedNow} pcs
              </span>
              <span className="chip muted" style={{ fontSize: 12 }}>
                Already decided {balance.totalDecided} / {balance.totalRequired}
              </span>
              <span className={'chip ' + (balance.totalOutstanding === 0 ? 'ready' : 'review')} style={{ fontSize: 12 }}>
                Balance after commit: {balance.totalOutstanding} pcs
              </span>
            </div>
            <div style={{ flex: 1 }} />
            {revision && (
              <button className="btn btn-ghost btn-sm" disabled={committing} onClick={cancelRevision}>
                Cancel revision
              </button>
            )}
            <button className="btn btn-secondary btn-sm" disabled={committing} onClick={() => setPicks({})}>Clear picks</button>
            <button className="btn btn-accent" disabled={committing} onClick={commitInlineDecision}>
              {committing
                ? 'Committing…'
                : revision
                ? `Commit as Rev ${revision.nextRevision}`
                : `Commit ${balance.linesWithPicks} line${balance.linesWithPicks !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}

      {balance.totalPickedNow === 0 && (
        <div className="banner ready" style={{ marginTop: 20 }}>
          <span>✓</span>
          <div>
            Coverage {data.summary.coveragePct}% · Click a supplier cell on any line to start awarding quantities.
          </div>
          <Link to={`/rfq/${data.rfq._id}/decide`}>
            <button className="btn btn-sm btn-secondary">Open Decide tab →</button>
          </Link>
        </div>
      )}
    </div>
  );
}

// ─── Supplier-wise table ──────────────────────────────────────────────────
// rows = RFQ lines, columns = suppliers
function SupplierWiseTable({ rows, suppliers, picks = {}, togglePick, updatePick, lineRemaining, baseCurrency = 'USD' }) {
  return (
    <div className="matrix-wrap">
      <table className="matrix">
        <thead>
          <tr>
            <th className="sticky col-line">Line</th>
            <th className="sticky col-part">Part No</th>
            <th>Qty / decided</th>
            {suppliers.map((s) => (
              <th key={s.envelope._id}>{s.supplier.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ line, cellsBySupplier, isDecided, rankedOnLandedCost }) => {
            const priced = suppliers
              .map((s) => cellsBySupplier[String(s.supplier._id)])
              .filter((c) => c && c.quoteLine)
              .map((c) => c.quoteLine);
            const cheapest = cheapestOf(priced, baseCurrency, { preferLanded: !!rankedOnLandedCost });
            const lid = String(line._id);
            const linePicks = picks[lid] || [];
            const required = Number(line.quantity) || 0;
            const decided = Number(line.decidedQuantity) || 0;
            const remaining = lineRemaining ? lineRemaining(line) : Math.max(0, required - decided);
            const pickedNow = linePicks.reduce((a, p) => a + (Number(p.quantity) || 0), 0);
            return (
              <tr key={line._id}>
                <td className="sticky col-line">{line.lineNumber}</td>
                <td className="sticky col-part mono">{line.partNumber}</td>
                <td className="mono" style={{ fontSize: 12 }}>
                  <div>{decided}/{required}</div>
                  {remaining === 0 && <span className="chip ready" style={{ fontSize: 10 }}>Complete</span>}
                  {pickedNow > 0 && (
                    <span className={'chip ' + (pickedNow > remaining ? 'risk' : 'primary')} style={{ fontSize: 10 }}>
                      {pickedNow > remaining ? 'Over!' : `+${pickedNow} pick`}
                    </span>
                  )}
                </td>
                {suppliers.map((s) => {
                  const cell = cellsBySupplier[String(s.supplier._id)];
                  const sid = String(s.supplier._id);
                  const pick = linePicks.find((p) => String(p.supplierId) === sid);
                  return (
                    <td key={s.envelope._id}>
                      {renderCell(cell, cheapest, {
                        line,
                        supplierId: sid,
                        pick,
                        remaining,
                        togglePick,
                        updatePick,
                        // `canPick` also covers "already picked", so the remove
                        // control is reachable when remaining is 0. Without this a
                        // pick that consumed the last of the quantity could not be
                        // taken back — the row locked itself.
                        canPick: !!(cell && cell.quoteLine) && (remaining > 0 || !!pick),
                        // Award context, so a losing cell can be dimmed and the
                        // winner can show its share of the required qty.
                        lineDecided: isDecided,
                        requiredQty: required,
                        baseCurrency,
                        validity: cell?.validity,
                      })}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Line-wise table ──────────────────────────────────────────────────────
// rows = RFQ lines, columns = best-metric stripe per supplier (price / lead / quality)
function LineWiseTable({ rows, suppliers, baseCurrency = 'USD' }) {
  return (
    <div className="matrix-wrap">
      <table className="matrix">
        <thead>
          <tr>
            <th className="sticky col-line">Line</th>
            <th className="sticky col-part">Part No</th>
            <th>Qty</th>
            {suppliers.map((s) => (
              <React.Fragment key={s.envelope._id}>
                {/* Header carries the RFQ currency, not a dollar sign. */}
                <th title={s.supplier.name + ' — Price (' + baseCurrency + ')'}>{s.supplier.name.slice(0, 8)} {baseCurrency}</th>
                <th title={s.supplier.name + ' — Lead'}>{s.supplier.name.slice(0, 8)} ⏱</th>
                <th title={s.supplier.name + ' — Quality'}>{s.supplier.name.slice(0, 8)} Q</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ line, cellsBySupplier, rankedOnLandedCost }) => {
            const priced = suppliers
              .map((s) => cellsBySupplier[String(s.supplier._id)])
              .filter((c) => c && c.quoteLine)
              .map((c) => c.quoteLine);
            const cheapest = cheapestOf(priced, baseCurrency, { preferLanded: !!rankedOnLandedCost });
            return (
              <tr key={line._id}>
                <td className="sticky col-line">{line.lineNumber}</td>
                <td className="sticky col-part mono">{line.partNumber}</td>
                <td className="mono">{line.quantity}</td>
                {suppliers.map((s) => {
                  const cell = cellsBySupplier[String(s.supplier._id)];
                  const q = cell?.quoteLine;
                  const isBest =
                    cheapest &&
                    q &&
                    String(cheapest._id) === String(q._id) &&
                    q.qualityStatus === 'QualityReady';
                  const statusClass = q
                    ? q.qualityStatus === 'QualityReady'
                      ? 'ready'
                      : q.qualityStatus === 'ReviewRequired'
                      ? 'review'
                      : 'risk'
                    : '';
                  return (
                    <React.Fragment key={s.envelope._id}>
                      <td className={'mono lw-cell' + (isBest ? ' lw-best' : '')}>
                        {q ? (
                          <>
                            {isBest && <span className="best" aria-label="Best value">★</span>}
                            {formatAmount(q.unitPrice, q.currency)}
                          </>
                        ) : (
                          <span className="cell-dash">—</span>
                        )}
                      </td>
                      <td className="lw-cell">
                        {q ? formatQuoteLeadTime(q) : <span className="cell-dash">—</span>}
                      </td>
                      <td className="lw-cell">
                        {q ? (
                          <span
                            className={'chip ' + statusClass}
                            style={{ fontSize: 10 }}
                            title={qualityTitle(q)}
                            aria-label={qualityTitle(q)}
                          >
                            {statusClass === 'ready' ? '✓' : statusClass === 'review' ? '~' : '✗'}
                          </span>
                        ) : (
                          <span className="cell-dash">—</span>
                        )}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Cell renderer (supplier-wise) ────────────────────────────────────────
// `interactive` (optional): { line, supplierId, pick, remaining, togglePick, updatePick, canPick }
const DECLINE_LABELS = {
  Obsolete: 'Obsolete',
  NoStock: 'No stock',
  NotApprovedSource: 'Not an approved source',
  MinimumOrderNotMet: 'Below their MOQ',
  LeadTimeUnacceptable: 'Lead time too long',
  CannotMeetCertification: 'Cannot certify',
  NoLongerSupplied: 'No longer supplied',
  PriceNotCompetitive: 'Declined to bid',
  Other: 'Declined',
};

function renderCell(cell, cheapest, interactive) {
  if (!cell || !cell.quoteLine) {
    // An explicit decline is very different information from silence (#15), so
    // say which it is rather than showing "No quote" for both.
    const d = cell?.decline;
    if (d) {
      return (
        <div className="cell missing" title={d.note ? `${DECLINE_LABELS[d.reason] || d.reason}: ${d.note}` : DECLINE_LABELS[d.reason] || d.reason}>
          <span className="chip muted" style={{ fontSize: 10 }}>
            {DECLINE_LABELS[d.reason] || d.reason}
          </span>
          {d.note && <div style={{ fontSize: 10, color: 'var(--ink-500)', marginTop: 2 }}>{d.note}</div>}
        </div>
      );
    }
    return <div className="cell missing">No response</div>;
  }
  const q = cell.quoteLine;
  const status =
    q.qualityStatus === 'QualityReady'
      ? 'ready'
      : q.qualityStatus === 'ReviewRequired'
      ? 'review'
      : 'risk';
  const isBest =
    cheapest &&
    String(cheapest._id) === String(q._id) &&
    q.qualityStatus === 'QualityReady';
  const revTooltip = q.revision
    ? `based on rev ${q.revision}${q.createdAt ? ` as of ${new Date(q.createdAt).toLocaleDateString()}` : ''}`
    : undefined;
  const isPicked = !!interactive?.pick;
  // Committed award state, distinct from an in-session pick:
  //   cell-awarded  = this supplier won this line (committed decision)
  //   cell-not-awarded = another supplier won it, so this quote lost
  const award = cell.award || null;
  const isAwarded = !!award;
  const acks = splitAcknowledgements(award?.riskFlagsAccepted);
  const lineDecided = !!interactive?.lineDecided;
  const isLoser = lineDecided && !isAwarded;
  // Combine the revision provenance and the quality reason into one tooltip.
  const cellTitle = [
    isAwarded ? `Chosen${award.reason ? ` — ${award.reason}` : ''}` : null,
    qualityTitle(q),
    revTooltip,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div
      className={
        'cell ' + status +
        (isPicked ? ' cell-picked' : '') +
        (isAwarded ? ' cell-awarded' : '') +
        (isLoser ? ' cell-not-awarded' : '')
      }
      title={cellTitle}
    >
      {/* Committed award banner — the single clearest signal on the tab. */}
      {isAwarded && (
        <div className="cell-award-label">
          ✓ Chosen
          {award.quantity != null && interactive?.requiredQty
            ? ` · ${Number(award.quantity).toLocaleString()} of ${Number(interactive.requiredQty).toLocaleString()}`
            : award.quantity != null
            ? ` · ${Number(award.quantity).toLocaleString()} pcs`
            : ''}
          {isBest ? ' · Best price' : ''}
        </div>
      )}
      {/* An award made over a quality risk must be visible on the line itself, not
          only in the audit trail (R2) — otherwise the next person to read Compare
          sees a normal win. The acknowledgement the buyer typed is the tooltip.

          The two acknowledgements are shown as two chips because they are two
          different claims. One chip driven by "the array is non-empty" labelled an
          expiry-only award "risk accepted", asserting a quality sign-off that never
          happened. */}
      {isAwarded && acks.quality.length > 0 && (
        <div
          className="chip risk"
          style={{ fontSize: 9, marginTop: 2 }}
          title={acks.quality.join(' · ')}
        >
          risk accepted
        </div>
      )}
      {isAwarded && acks.expiry.length > 0 && (
        <div
          className="chip risk"
          style={{ fontSize: 9, marginTop: 2 }}
          title={acks.expiry.join(' · ')}
        >
          expiry accepted
        </div>
      )}
      {isBest && <span className="best" aria-label="Best value">★</span>}
      {q.revision > 1 && (
        <span className="chip info" style={{ fontSize: 9, position: 'absolute', top: 2, right: 2 }}>
          r{q.revision}
        </span>
      )}
      {/* Price shown as quoted. When the supplier quoted in another currency we
          also show the RFQ-currency equivalent and the rate used (#09). */}
      <div className="price">{formatAmount(q.unitPrice, q.currency)}</div>
      {q.currency && interactive?.baseCurrency && q.currency !== interactive.baseCurrency && (
        q.isConvertible === false ? (
          <div className="chip risk" style={{ fontSize: 10, marginTop: 2 }}>
            {unconvertibleNote(q, interactive.baseCurrency) || `No ${interactive.baseCurrency} rate`} — not compared
          </div>
        ) : (
          <div className="mono" style={{ fontSize: 10, color: 'var(--ink-500)', marginTop: 2 }}>
            = {formatAmount(q.unitPriceBase, interactive.baseCurrency)} @ {q.fxRate}
          </div>
        )
      )}
      <div className="meta">
        {formatQuoteLeadTime(q)} · {q.conditionQuoted?.slice(0, 2) || '—'}
        {q.incoterm ? ` · ${q.incoterm}` : ''}{q.countryOfOrigin ? ` ${q.countryOfOrigin}` : ''}
      </div>
      {/* Price validity (#14): flag a stale price BEFORE it gets picked. */}
      {interactive?.validity && interactive.validity.state !== 'Unknown' && interactive.validity.state !== 'Valid' && (
        <div
          className={'chip ' + (interactive.validity.state === 'Expired' ? 'risk' : 'review')}
          style={{ fontSize: 9, marginTop: 2 }}
          title={interactive.validity.label}
        >
          {interactive.validity.state === 'Expired' ? 'price expired' : 'expires soon'}
        </div>
      )}
      {/* Delivered unit cost, and why it's missing when it is (#13). */}
      {q.landedUnitBase != null ? (
        <div className="mono" style={{ fontSize: 10, color: 'var(--ink-700)' }} title={q.landedCostNote}>
          landed {formatAmount(q.landedUnitBase, interactive?.baseCurrency)}/unit
        </div>
      ) : q.landedCostNote ? (
        <div style={{ fontSize: 10, color: 'var(--ink-500)' }} title={q.landedCostNote}>
          landed n/a
        </div>
      ) : null}
      <div className="icons">
        <span
          className={
            q.qplCompliant === 'Yes'
              ? 'chip ready'
              : q.qplCompliant === 'No'
              ? 'chip risk'
              : 'chip review'
          }
        >
          QPL {q.qplCompliant?.charAt(0)}
        </span>
        <span className={q.cocAvailable ? 'chip ready' : 'chip risk'}>
          CoC {q.cocAvailable ? '✓' : '✗'}
        </span>
        {q.isAlternate && <span className="chip review">ALT</span>}
        {q.excessQty > 0 && <span className="chip muted">MOQ+{q.excessQty}</span>}
      </div>

      {/* Why this line is flagged — visible, not hover-only, for anything that
          isn't clean. A quality manager should never have to guess. */}
      {q.qualityRule && q.qualityStatus !== 'QualityReady' && (
        <div
          className="cell-reason"
          style={{ fontSize: 10, lineHeight: 1.25, marginTop: 3, color: 'var(--ink-700)' }}
        >
          {q.qualityRule}
        </div>
      )}

      {/* Inline-decision controls — always-visible qty input, then Pick or per-cell reason */}
      {interactive && interactive.canPick && (
        <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type="number"
              min={0}
              max={interactive.remaining}
              className="input"
              placeholder="Qty"
              value={interactive.draftQty ?? (isPicked ? interactive.pick.quantity : '')}
              onChange={(e) => {
                const raw = e.target.value;
                const n = raw === '' ? 0 : Number(raw);
                if (n > 0 && !isPicked) {
                  interactive.togglePick(interactive.line, interactive.supplierId, q._id);
                  // togglePick defaults to remaining; immediately overwrite with the user's value
                  interactive.updatePick(String(interactive.line._id), interactive.supplierId, { quantity: n });
                } else if (isPicked) {
                  if (n === 0 && raw !== '') {
                    // Treat 0 as unpick
                    interactive.togglePick(interactive.line, interactive.supplierId, q._id);
                  } else {
                    interactive.updatePick(String(interactive.line._id), interactive.supplierId, { quantity: n });
                  }
                }
              }}
              style={{ width: 60, padding: '2px 4px', fontSize: 11 }}
            />
            <span style={{ fontSize: 10, color: 'var(--ink-500)' }}>of {interactive.remaining}</span>
            {!isPicked && (
              <button
                className="btn btn-sm"
                style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px' }}
                onClick={() => interactive.togglePick(interactive.line, interactive.supplierId, q._id)}
              >Pick</button>
            )}
            {isPicked && (
              <button
                className="btn btn-ghost btn-sm"
                style={{ marginLeft: 'auto', padding: '0 4px', fontSize: 14 }}
                title="Remove pick"
                onClick={() => interactive.togglePick(interactive.line, interactive.supplierId, q._id)}
              >×</button>
            )}
          </div>
          {isPicked && (
            <select
              className="select"
              value={interactive.pick.reason}
              onChange={(e) => interactive.updatePick(String(interactive.line._id), interactive.supplierId, { reason: e.target.value })}
              style={{ fontSize: 11, padding: '2px 4px', background: 'var(--primary-tint)' }}
            >
              {REASONS.map((r) => <option key={r}>{r}</option>)}
            </select>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Base-currency money, rendered with the RFQ's own currency.
 *
 * This hardcoded `currency: 'USD'` for every total on the tab. On an EUR RFQ where
 * every supplier quoted EUR, the "Totals in {baseCurrency}" tile is also hidden
 * (it only renders for multi-currency or unconvertible sets), so EUR 7,000 was
 * displayed as "$7,000" with nothing anywhere on the screen saying EUR. The
 * currency is now a required argument — see src/lib/money.js.
 */
function formatMoney(n, currency) {
  return fmt(n, currency, { maximumFractionDigits: 0 });
}

const QUALITY_LABEL = {
  QualityReady: 'Quality ready',
  ReviewRequired: 'Review required',
  QualityRisk: 'Quality risk',
};

/**
 * Tooltip/aria text for a quality chip: the status plus the rule that produced
 * it, so a Risk or Review chip is never unexplained (finding #10).
 */
export function qualityTitle(q) {
  if (!q) return '';
  const label = QUALITY_LABEL[q.qualityStatus] || q.qualityStatus || 'Unknown';
  return q.qualityRule ? `${label} — ${q.qualityRule}` : label;
}

/**
 * Render a lead time already normalized to days.
 * 0 days means the part ships from stock — say so rather than printing "0d".
 * null means nothing was quoted — that is "—", never 0 and never a sentinel.
 */
/**
 * Format an amount with its currency code (#09). Quotes are captured in the
 * supplier's currency, so a bare "$" prefix was wrong for EUR/GBP/INR quotes.
 */
export function formatAmount(amount, currency) {
  // Delegates to fmt(), which marks a MISSING currency visibly instead of
  // defaulting to USD. The `|| 'USD'` that used to live here was the one remaining
  // silent-dollar path in this file, reachable on pre-#09 rows where
  // `quoteLine.currency` is unset (the schema has no default).
  return fmt(amount, currency);
}

export function formatLeadDays(days) {
  if (days == null) return '—';
  if (days === 0) return 'Stock';
  if (days % 7 === 0 && days >= 14) return `${days / 7}w`;
  return `${days}d`;
}

/**
 * Render a single quote line's own lead time. Converts Weeks → days first so
 * the two comparison tables can no longer disagree (one printed "—" for a
 * 0-day line while the other printed "0d").
 */
export function formatQuoteLeadTime(q) {
  if (!q) return '—';
  if (q.leadTimeType === 'ExStock' && q.leadTimeValue == null) return 'Stock';
  if (q.leadTimeValue == null || q.leadTimeValue === '') return '—';
  const v = Number(q.leadTimeValue);
  if (!Number.isFinite(v)) return '—';
  return formatLeadDays(q.leadTimeUnit === 'Weeks' ? v * 7 : v);
}
